import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import { useBillingStatus } from "../hooks/useBillingStatus.js";
import { getRules, createRule, updateRule, deleteRule, testRule } from "../api.js";
import type { Rule } from "@tuit/shared-types";
import styles from "../styles/pages/SettingsPage.module.css";

export function RulesPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { status: billing } = useBillingStatus();
  const [rules, setRules] = useState<Rule[]>([]);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { matchCount: number; testedCount: number }>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn) return;
    getRules().then(setRules).catch(() => {});
  }, [isSignedIn]);

  if (!isLoaded || !isSignedIn) return null;

  const canRules = billing?.capabilities.canWebhooks ?? false;

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const r = await createRule({
        name: name.trim(),
        conditionLogic: "any",
        conditions: [{ field: "title", operator: "contains", value: "" }],
        actions: [{ action: "star" }],
      });
      setRules((prev) => [...prev, r]);
      setName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create rule");
    } finally {
      setAdding(false);
    }
  }

  async function handleToggle(rule: Rule) {
    setBusyId(rule.id);
    try {
      await updateRule(rule.id, { enabled: !rule.enabled });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: !rule.enabled } : r)));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(id: string) {
    setBusyId(id);
    try {
      await deleteRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setBusyId(null);
    }
  }

  async function handleTest(id: string) {
    setBusyId(`test_${id}`);
    try {
      const result = await testRule(id);
      setTestResult((prev) => ({ ...prev, [id]: result }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/profile" className={styles.back}>← Profile</Link>
      </nav>
      <div className={styles.inner}>
        <h1 className={styles.heading}>Automation Rules</h1>
        <p className={styles.sub}>Automatically star, hide, or route posts based on conditions.</p>

        {!canRules ? (
          <div className={styles.upsell}>
            <p>Automation rules are available on the <strong>Pro</strong> plan.</p>
            <Link to="/premium" className={styles.upsellLink}>Upgrade now →</Link>
          </div>
        ) : (
          <>
            <form className={styles.card} onSubmit={handleAdd}>
              <div className={styles.inlineFields}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Rule name (e.g. Star all AI posts)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <button type="submit" className={styles.primaryBtn} disabled={adding || !name.trim()}>
                  {adding ? "Creating…" : "Create rule"}
                </button>
              </div>
              {error && <p className={styles.error}>{error}</p>}
              <p className={styles.hint}>Rules are created with a default condition. Edit conditions via the API for now.</p>
            </form>

            {rules.length > 0 && (
              <ul className={styles.list}>
                {rules.map((rule) => (
                  <li key={rule.id} className={styles.listItem}>
                    <span className={`${styles.dot} ${rule.enabled ? styles.dotGreen : styles.dotGray}`} />
                    <div className={styles.listItemMain}>
                      <span className={styles.listItemText}>{rule.name}</span>
                      {testResult[rule.id] != null && (() => {
                        const tr = testResult[rule.id]!;
                        return (
                          <span className={styles.listItemMeta}>
                            {tr.matchCount} / {tr.testedCount} recent posts matched
                          </span>
                        );
                      })()}
                    </div>
                    <div className={styles.rowActions}>
                      <button type="button" className={styles.smallBtn} onClick={() => handleTest(rule.id)} disabled={busyId === `test_${rule.id}`}>Test</button>
                      <button type="button" className={styles.smallBtn} onClick={() => handleToggle(rule)} disabled={busyId === rule.id}>
                        {rule.enabled ? "Disable" : "Enable"}
                      </button>
                      <button type="button" className={styles.deleteBtn} onClick={() => handleDelete(rule.id)} disabled={busyId === rule.id}>Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {rules.length === 0 && <p className={styles.hint}>No rules yet.</p>}
          </>
        )}
      </div>
    </div>
  );
}
